/**
 * WCAG 2.1 relative luminance and contrast, and the one thing this app needs it
 * for at runtime.
 *
 * The order-status palette in `src/lib/ui-tokens` is **semantic** — a guest
 * learns that amber means "being made" and green means "ready", and those hues
 * are shared with the KDS and the waiter app — so it is not ours to restyle.
 * But the badge that carries it was hardcoded to white text, and white on
 * `PREPARING` (#f59e0b) measures 2.15:1: four of the eight states failed AA
 * outright.
 *
 * So the background stays exactly what the token says and only the *ink* is
 * chosen, per swatch, by measurement.
 */

const INK_ON_LIGHT = '#10100C';
const INK_ON_DARK = '#FFFFFF';

function channel(value: number): number {
  const v = value / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** Accepts `#rgb` and `#rrggbb`. Anything else is treated as mid grey. */
export function relativeLuminance(hex: string): number {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? [...raw].map((c) => c + c).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => channel(parseInt(full.slice(i, i + 2), 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The more legible of near-black and white on `background`.
 *
 * Near-black rather than pure black: #10100C is the brand's own ink family, and
 * on the amber and green swatches it beats pure white by 3–4x while still
 * looking like type rather than a hole in the badge.
 */
export function readableInk(background: string): string {
  return contrastRatio(INK_ON_LIGHT, background) >= contrastRatio(INK_ON_DARK, background)
    ? INK_ON_LIGHT
    : INK_ON_DARK;
}
